/**
 * The floating Ask Velora widget's state: open or closed and where it sits.
 * Remembered per browser session (sessionStorage), so it survives page
 * changes and reloads but a new session starts closed. The palette's
 * "Ask Velora" hands its question over with `openAssistant(question)`; the
 * question stays in memory only (it can name a patient, BUILD-PLAN 2.8).
 *
 * The panel's size (the user drags its edges) is a preference, so it is kept
 * across sessions in localStorage. Full screen is in memory only: page
 * changes keep it, a reload brings the floating panel back.
 */
import { useSyncExternalStore } from 'react';

export interface WidgetPosition {
  /** Distance from the right edge of the window, px. */
  right: number;
  /** Distance from the bottom edge of the window, px. */
  bottom: number;
}

export interface WidgetSize {
  width: number;
  height: number;
}

export interface WidgetState {
  open: boolean;
  pos: WidgetPosition;
  /** The floating panel's size as the user last left it (fitted to the window when drawn). */
  size: WidgetSize;
  /** Expanded to fill the window, with a small margin all round. */
  full: boolean;
  /** A question to ask as soon as the panel is ready; `nonce` makes repeats distinct. */
  pending: { question: string; nonce: number } | null;
}

export const WIDGET_KEY = 'velora.assistant.widget';
export const SIZE_KEY = 'velora.assistant.widget.size';
export const DEFAULT_POS: WidgetPosition = { right: 20, bottom: 20 };
export const DEFAULT_SIZE: WidgetSize = { width: 400, height: 600 };
/** The smallest useful chat. The largest is the window less the margin, capped at MAX_SIZE. */
export const MIN_SIZE: WidgetSize = { width: 340, height: 380 };
export const MAX_SIZE: WidgetSize = { width: 1600, height: 1400 };

function bounded(v: number, min: number, max: number): number {
  return Math.round(Math.min(max, Math.max(min, v)));
}

function loadSize(): WidgetSize {
  try {
    const raw = JSON.parse(localStorage.getItem(SIZE_KEY) ?? 'null') as {
      width?: unknown;
      height?: unknown;
    } | null;
    if (
      raw &&
      typeof raw.width === 'number' &&
      typeof raw.height === 'number' &&
      Number.isFinite(raw.width) &&
      Number.isFinite(raw.height)
    ) {
      return {
        width: bounded(raw.width, MIN_SIZE.width, MAX_SIZE.width),
        height: bounded(raw.height, MIN_SIZE.height, MAX_SIZE.height),
      };
    }
  } catch {
    /* storage blocked or a broken value: the default size */
  }
  return DEFAULT_SIZE;
}

function saveSize(size: WidgetSize): void {
  try {
    localStorage.setItem(SIZE_KEY, JSON.stringify(size));
  } catch {
    /* storage blocked: the size still holds for this page */
  }
}

function load(): WidgetState {
  const size = loadSize();
  try {
    const raw = JSON.parse(sessionStorage.getItem(WIDGET_KEY) ?? '{}') as Partial<WidgetState>;
    const pos =
      raw.pos && Number.isFinite(raw.pos.right) && Number.isFinite(raw.pos.bottom)
        ? { right: Math.max(0, raw.pos.right), bottom: Math.max(0, raw.pos.bottom) }
        : DEFAULT_POS;
    return { open: raw.open === true, pos, size, full: false, pending: null };
  } catch {
    return { open: false, pos: DEFAULT_POS, size, full: false, pending: null };
  }
}

let state: WidgetState | null = null;
const listeners = new Set<() => void>();

function current(): WidgetState {
  state ??= load();
  return state;
}

function update(patch: Partial<WidgetState>): void {
  state = { ...current(), ...patch };
  try {
    sessionStorage.setItem(WIDGET_KEY, JSON.stringify({ open: state.open, pos: state.pos }));
  } catch {
    /* storage blocked: the state still works for this page */
  }
  for (const l of listeners) l();
}

let nonce = 0;

export const assistantWidget = {
  get: current,
  subscribe(l: () => void) {
    listeners.add(l);
    return () => listeners.delete(l);
  },
  setOpen: (open: boolean) => update({ open }),
  toggle: () => update({ open: !current().open }),
  move: (pos: WidgetPosition) => update({ pos }),
  /**
   * A resize from the panel's edges: the new size, and the anchor that keeps
   * the opposite edge where it was. Only `commit` writes localStorage (once,
   * at the end of a drag).
   */
  resize: (size: WidgetSize, pos: WidgetPosition, commit = true) => {
    if (commit) saveSize(size);
    update({ size, pos });
  },
  setFull: (full: boolean) => update({ full }),
  consume: () => update({ pending: null }),
  /** Test seam: forget the in-memory copy (the next read loads storage again). */
  reset: () => {
    state = null;
  },
};

/** Opens the widget, optionally asking `question` right away. */
export function openAssistant(question?: string): void {
  const q = question?.trim();
  update({ open: true, pending: q ? { question: q, nonce: ++nonce } : current().pending });
}

export function useAssistantWidget(): WidgetState {
  return useSyncExternalStore(assistantWidget.subscribe, assistantWidget.get, assistantWidget.get);
}

/**
 * Keeps a panel of `size` inside a `viewport`, `margin` px from every edge:
 * a position saved on a wide screen never strands the widget off a small one.
 */
export function clampPosition(
  pos: WidgetPosition,
  size: { width: number; height: number },
  viewport: { width: number; height: number },
  margin = 8,
): WidgetPosition {
  const maxRight = Math.max(margin, viewport.width - size.width - margin);
  const maxBottom = Math.max(margin, viewport.height - size.height - margin);
  return {
    right: Math.round(Math.min(Math.max(margin, pos.right), maxRight)),
    bottom: Math.round(Math.min(Math.max(margin, pos.bottom), maxBottom)),
  };
}

/**
 * The panel's size fitted to a `viewport`: at least MIN_SIZE (unless the
 * window itself is smaller), at most the window less `margin` on each side.
 */
export function fitSize(
  size: WidgetSize,
  viewport: { width: number; height: number },
  margin = 8,
): WidgetSize {
  const maxW = Math.max(0, Math.min(MAX_SIZE.width, viewport.width - margin * 2));
  const maxH = Math.max(0, Math.min(MAX_SIZE.height, viewport.height - margin * 2));
  return {
    width: bounded(size.width, Math.min(MIN_SIZE.width, maxW), maxW),
    height: bounded(size.height, Math.min(MIN_SIZE.height, maxH), maxH),
  };
}
